import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { t } from 'i18next';
import { useSnackbar } from 'notistack';
import Tooltip from '@mui/material/Tooltip';
import TabMenu from '../util/TabMenu';
import TabPanel from '@mui/lab/TabPanel';

interface Role { id: number; title: string; color?: string; }
interface User {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  isActive?: boolean;
  roles?: Role[];
  role?: Role;
}

interface ResetState {
  userId: number | null;
  userName: string;
  password: string;
  confirm: string;
  loading: boolean;
  error: string;
  success: boolean;
}

interface RoleDropdown {
  userId: number;
  rect: DOMRect;
  current: number[];
  saving: boolean;
}

interface DeleteState {
  userId: number;
  userName: string;
  loading: boolean;
}

interface Dependency {
  section: string;
  reason: string;
  count: number;
  items?: string[];
}

interface DeleteBlocked {
  userName: string;
  dependencies: Dependency[];
}

type SortField = 'name' | 'email';
type SortDir   = 'asc' | 'desc';

const RESET_INIT: ResetState = { userId: null, userName: '', password: '', confirm: '', loading: false, error: '', success: false };

const UsersManagement: React.FC<{ access: any }> = ({ access }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [users, setUsers]                 = useState<User[]>([]);
  const [usersNotActive, setUsersNotActive] = useState<User[]>([]);
  const [allRoles, setAllRoles]           = useState<Role[]>([]);
  const [reset, setReset]                 = useState<ResetState>(RESET_INIT);
  const [dropdown, setDropdown]           = useState<RoleDropdown | null>(null);
  const [usersTab, setUsersTab]           = useState<'active' | 'inactive'>('active');
  const [sortField, setSortField]         = useState<SortField>('name');
  const [sortDir, setSortDir]             = useState<SortDir>('asc');
  const [deleteState, setDeleteState]     = useState<DeleteState | null>(null);
  const [deleteBlocked, setDeleteBlocked] = useState<DeleteBlocked | null>(null);
  const dropRef = useRef<HTMLDivElement>(null);

  const canEditRoles     = access?.users_edit === true || access?.users_edit === 1;
  const canResetPassword = access?.users_edit === true || access?.users_edit === 1;
  const canViewInactive  = access?.users_view_all === true || access?.users_view_all === 1;
  const canDelete        = access?.users_delete === true || access?.users_delete === 1;

  const fetchUsers = useCallback(async () => {
    try {
      const { data } = await axios.get('users');
      setUsers(Array.isArray(data) ? data : []);
    } catch {}
  }, []);

  const fetchUsersNotActive = useCallback(async () => {
    try {
      const { data } = await axios.get('users/inactive');
      setUsersNotActive(Array.isArray(data) ? data : []);
    } catch {}
  }, []);

  const fetchRoles = useCallback(async () => {
    try {
      const { data } = await axios.get('roles');
      setAllRoles(Array.isArray(data) ? data : []);
    } catch {}
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);
  useEffect(() => { if (canViewInactive) fetchUsersNotActive(); }, [fetchUsersNotActive, canViewInactive]);
  useEffect(() => { if (canEditRoles) fetchRoles(); }, [fetchRoles, canEditRoles]);

  const refreshAll = () => { fetchUsers(); if (canViewInactive) fetchUsersNotActive(); };

  // Close dropdown on outside click
  useEffect(() => {
    if (!dropdown) return;
    const handler = (e: MouseEvent) => {
      if (dropRef.current && !dropRef.current.contains(e.target as Node)) {
        setDropdown(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [dropdown]);

  // — Sorting —
  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sortedRows = (rows: User[]) => [...rows].sort((a, b) => {
    let va = '', vb = '';
    if (sortField === 'name') {
      va = `${a.first_name} ${a.last_name}`.toLowerCase();
      vb = `${b.first_name} ${b.last_name}`.toLowerCase();
    } else {
      va = (a.email || '').toLowerCase();
      vb = (b.email || '').toLowerCase();
    }
    return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
  });

  const sortIcon = (field: SortField) => {
    if (sortField !== field) return <span style={{ opacity: 0.3, marginLeft: 4 }}>↕</span>;
    return <span style={{ marginLeft: 4 }}>{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  // — Reset password —
  const openReset  = (user: User) => setReset({ ...RESET_INIT, userId: user.id, userName: `${user.first_name} ${user.last_name}` });
  const closeReset = () => setReset(RESET_INIT);

  const submitReset = async () => {
    if (!reset.password || !/^(?=.*[a-z])(?=.*[A-Z])(?=.*[!@#$%^&*()\-_=+[\]{};':"\\|,.<>/?`~]).{8,}$/.test(reset.password)) { setReset(r => ({ ...r, error: t('PasswordRequirements') as string })); return; }
    if (reset.password !== reset.confirm)              { setReset(r => ({ ...r, error: t('PasswordsDoNotMatch') as string })); return; }
    setReset(r => ({ ...r, loading: true, error: '' }));
    try {
      await axios.put(`password/${reset.userId}`, { password: reset.password, password_confirm: reset.confirm });
      setReset(r => ({ ...r, loading: false, success: true }));
      setTimeout(closeReset, 1400);
    } catch (e: any) {
      setReset(r => ({ ...r, loading: false, error: e?.response?.data?.message || t('Error') as string }));
    }
  };

  // — Delete user —
  const openDelete = (user: User) =>
    setDeleteState({ userId: user.id, userName: `${user.first_name} ${user.last_name}`, loading: false });

  const confirmDelete = async () => {
    if (!deleteState) return;
    setDeleteState(s => s ? { ...s, loading: true } : s);
    try {
      const resp = await axios.post(`delete-user/${deleteState.userId}`);
      const data = resp.data;
      setDeleteState(null);
      if (data.hasBlocking && data.dependencies?.length) {
        setDeleteBlocked({ userName: deleteState.userName, dependencies: data.dependencies });
      } else if (data.isDeleted) {
        enqueueSnackbar(t('UserDeleted') as string || 'User deleted', { variant: 'success' });
        refreshAll();
      } else {
        enqueueSnackbar(data.message || 'Cannot delete user', { variant: 'warning' });
        refreshAll();
      }
    } catch (e: any) {
      setDeleteState(s => s ? { ...s, loading: false } : s);
      enqueueSnackbar(e?.response?.data?.message || t('Error') as string, { variant: 'error' });
    }
  };

  // — Role dropdown —
  const openDropdown = (e: React.MouseEvent, user: User) => {
    e.stopPropagation();
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const ids  = user.roles?.map(r => r.id) ?? (user.role ? [user.role.id] : []);
    setDropdown({ userId: user.id, rect, current: ids, saving: false });
  };

  const toggleRole = (id: number) =>
    setDropdown(p => !p ? p : ({ ...p, current: p.current.includes(id) ? p.current.filter(x => x !== id) : [...p.current, id] }));

  const saveRoles = async () => {
    if (!dropdown) return;
    setDropdown(p => p ? { ...p, saving: true } : p);
    try {
      await axios.put(`update-user/${dropdown.userId}`, { rolesId: dropdown.current });
      enqueueSnackbar(t('RolesUpdated') as string, { variant: 'success' });
      setDropdown(null);
      fetchUsers();
    } catch {
      enqueueSnackbar(t('Error') as string, { variant: 'error' });
      setDropdown(p => p ? { ...p, saving: false } : p);
    }
  };

  const MAX_VISIBLE = 2;

  // Dropdown positioning
  const dropStyle = (() => {
    if (!dropdown) return {};
    const { rect } = dropdown;
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const panelH = Math.min(allRoles.length * 36 + 80, 320);
    if (spaceBelow < panelH && spaceAbove > panelH) {
      return { bottom: window.innerHeight - rect.top + 4, top: 'auto' as const };
    }
    return { top: rect.bottom + 4 };
  })();

  const headCellStyle: React.CSSProperties = {
    color: 'var(--text-color)',
    fontSize: 11,
    padding: '10px 14px',
    backgroundColor: 'var(--background-color)',
    borderBottom: '1px solid var(--border-input)',
    fontWeight: 600,
    textAlign: 'left',
    whiteSpace: 'nowrap',
  };

  const sortableHeadStyle: React.CSSProperties = {
    ...headCellStyle,
    cursor: 'pointer',
    userSelect: 'none',
  };

  const bodyCellStyle: React.CSSProperties = {
    color: 'var(--text-color)',
    fontSize: 13,
    padding: '8px 14px',
    backgroundColor: 'var(--btn-user-form)',
    borderBottom: '1px solid var(--border-input)',
  };

  const actionBtnStyle: React.CSSProperties = {
    fontSize: 11, padding: '2px 8px', borderRadius: 4,
    border: '1px solid var(--border-input)',
    background: 'var(--btn-user-form)',
    color: 'var(--text-color-calendar-week)',
    cursor: 'pointer', whiteSpace: 'nowrap',
  };

  const renderTable = (rows: User[], showDeactivate: boolean) => {
    const sorted = sortedRows(rows);
    const showDeleteBtn = !showDeactivate && canDelete; // delete only in inactive tab
    const hasActions = canResetPassword || canEditRoles || (showDeactivate) || showDeleteBtn;
    return (
      <div style={{ overflow: 'auto', maxHeight: 'calc(100vh - 260px)', borderRadius: 6, border: '1px solid var(--border-input)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '25%' }} />
            <col style={{ width: '35%' }} />
            <col />
            {hasActions && <col style={{ width: showDeleteBtn ? 160 : 120 }} />}
          </colgroup>
          <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
            <tr>
              <th style={sortableHeadStyle} onClick={() => toggleSort('name')}>
                {t('Name')}{sortIcon('name')}
              </th>
              <th style={sortableHeadStyle} onClick={() => toggleSort('email')}>
                {t('Email')}{sortIcon('email')}
              </th>
              <th style={headCellStyle}>{t('Roles')}</th>
              {hasActions && <th style={{ ...headCellStyle, textAlign: 'right' }} />}
            </tr>
          </thead>
          <tbody>
            {sorted.map(row => {
              const rowRoles = row.roles?.length ? row.roles : (row.role ? [row.role] : []);
              const visible  = rowRoles.slice(0, MAX_VISIBLE);
              const extra    = rowRoles.length - MAX_VISIBLE;

              const chips = (
                <div
                  style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center', cursor: (canEditRoles && showDeactivate) ? 'pointer' : 'default' }}
                  onClick={(canEditRoles && showDeactivate) ? (e) => openDropdown(e, row) : undefined}
                >
                  {visible.map(r => (
                    <span key={r.id} style={{
                      fontSize: 11, padding: '2px 7px', borderRadius: 4,
                      background: r.color ? `${r.color}22` : 'var(--btn-user-form)',
                      color: r.color ?? 'var(--text-color)',
                      border: `1px solid ${r.color ? `${r.color}55` : 'var(--border-input)'}`,
                      fontWeight: 500, whiteSpace: 'nowrap',
                    }}>
                      {r.title}
                    </span>
                  ))}
                  {extra > 0 && (
                    <span style={{
                      fontSize: 11, padding: '2px 6px', borderRadius: 4,
                      background: 'var(--btn-user-form)', color: 'var(--text-color-calendar-week)',
                      border: '1px solid var(--border-input)', fontWeight: 600,
                    }}>
                      +{extra}
                    </span>
                  )}
                  {rowRoles.length === 0 && (
                    <span style={{ fontSize: 11, color: 'var(--text-color-calendar-week)' }}>—</span>
                  )}
                  {(canEditRoles && showDeactivate) && (
                    <span style={{ fontSize: 10, color: 'var(--text-color-calendar-week)', marginLeft: 2 }}>✎</span>
                  )}
                </div>
              );

              return (
                <tr key={row.id}>
                  <td style={bodyCellStyle}>
                    <span style={{ fontSize: 13 }}>{row.first_name} {row.last_name}</span>
                  </td>
                  <td style={bodyCellStyle}>
                    <span style={{ fontSize: 13 }}>{row.email}</span>
                  </td>
                  <td style={bodyCellStyle}>
                    {rowRoles.length > MAX_VISIBLE ? (
                      <Tooltip
                        title={<div style={{ fontSize: 12, lineHeight: 1.6 }}>{rowRoles.map(r => r.title).join(', ')}</div>}
                        placement="top"
                        arrow
                      >
                        {chips}
                      </Tooltip>
                    ) : chips}
                  </td>
                  {hasActions && (
                    <td style={{ ...bodyCellStyle, textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end', alignItems: 'center' }}>
                        {showDeactivate && canEditRoles && (
                          <button
                            type="button"
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                await axios.put(`update-user/${row.id}`, { isActive: false });
                                refreshAll();
                              } catch {}
                            }}
                            style={actionBtnStyle}
                          >
                            {t('Deactivate')}
                          </button>
                        )}
                        {!showDeactivate && canEditRoles && (
                          <button
                            type="button"
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                await axios.put(`update-user/${row.id}`, { isActive: true });
                                refreshAll();
                              } catch {}
                            }}
                            style={actionBtnStyle}
                          >
                            {t('Reactivate')}
                          </button>
                        )}
                        {showDeactivate && canResetPassword && (
                          <Tooltip title={t('ResetPassword') as string} placement="left">
                            <button
                              type="button"
                              onClick={() => openReset(row)}
                              className="bg-[var(--btn-user-in)] text-[var(--text-color)] border-none rounded-[5px] cursor-pointer hover:bg-[var(--btn-user-hover)]"
                              style={{ padding: '3px 7px', fontSize: 14, lineHeight: 1 }}
                            >
                              🔑
                            </button>
                          </Tooltip>
                        )}
                        {showDeleteBtn && (
                          <Tooltip title={t('Delete') as string} placement="left">
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); openDelete(row); }}
                              className="bg-[var(--btn-user-in)] border-none rounded-[5px] cursor-pointer"
                              style={{ padding: '3px 7px', fontSize: 14, lineHeight: 1, color: '#ef4444' }}
                            >
                              🗑
                            </button>
                          </Tooltip>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="p-[16px]" style={{ position: 'relative' }}>
      <div className="text-[14px] font-semibold text-[var(--text-color)] mb-[12px]">
        {t('SettingsUsersTitle')}
      </div>

      <TabMenu
        value={usersTab}
        onChange={(_, v) => setUsersTab(v as 'active' | 'inactive')}
        tabs={[
          { label: t('ActiveUsers'), value: 'active' },
          { label: t('InactiveUsers'), value: 'inactive', hidden: !canViewInactive },
        ]}
      >
        <TabPanel value='active' sx={{ padding: '12px 0 0' }}>
          {renderTable(users, true)}
        </TabPanel>
        <TabPanel value='inactive' sx={{ padding: '12px 0 0' }}>
          {renderTable(usersNotActive, false)}
        </TabPanel>
      </TabMenu>

      {/* — Role dropdown (portal) — */}
      {dropdown && ReactDOM.createPortal(
        <div
          ref={dropRef}
          data-portal-modal
          onClick={e => e.stopPropagation()}
          style={{
            position: 'fixed',
            left: dropdown.rect.left,
            ...dropStyle,
            zIndex: 'var(--z-portal)' as any,
            background: 'var(--bg-form)',
            border: '1px solid var(--border-input)',
            borderRadius: 8,
            boxShadow: '0 8px 32px rgba(0,0,0,.18)',
            padding: 12,
            minWidth: 220,
            maxHeight: 320,
            overflowY: 'auto',
          }}
        >
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-color-calendar-week)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            {t('EditRoles')}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {allRoles.map(role => {
              const checked = dropdown.current.includes(role.id);
              return (
                <label
                  key={role.id}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
                    padding: '5px 6px', borderRadius: 5,
                    background: checked ? (role.color ? `${role.color}18` : 'var(--btn-user-form)') : 'transparent',
                    transition: 'background .12s',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleRole(role.id)}
                    style={{ accentColor: role.color || 'var(--btn-user-btn)', width: 14, height: 14, cursor: 'pointer', flexShrink: 0 }}
                  />
                  {role.color && (
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: role.color, flexShrink: 0 }} />
                  )}
                  <span style={{ fontSize: 12, color: role.color || 'var(--text-color)', fontWeight: checked ? 600 : 400 }}>
                    {role.title}
                  </span>
                </label>
              );
            })}
            {allRoles.length === 0 && (
              <span style={{ fontSize: 12, color: 'var(--text-color-calendar-week)' }}>No roles available</span>
            )}
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, marginTop: 10, paddingTop: 8, borderTop: '1px solid var(--border-input)' }}>
            <button type="button" onClick={() => setDropdown(null)}
              className="bg-[var(--btn-user-form)] text-[var(--text-color)] border border-[var(--border-input)] py-[4px] px-[10px] rounded-[5px] cursor-pointer text-[12px]">
              {t('Cancel')}
            </button>
            <button type="button" onClick={saveRoles} disabled={dropdown.saving}
              className="bg-[var(--btn-user-btn)] text-[var(--text-color)] border-none py-[4px] px-[12px] rounded-[5px] cursor-pointer text-[12px]">
              {dropdown.saving ? '...' : t('Save')}
            </button>
          </div>
        </div>,
        document.body
      )}

      {/* — Delete confirmation modal — */}
      {deleteState && ReactDOM.createPortal(
        <div
          data-portal-modal
          onClick={() => !deleteState.loading && setDeleteState(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 'var(--z-portal)' as any,
            background: 'rgba(0,0,0,0.25)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            className="bg-[var(--bg-form)] rounded-[8px] p-[24px]"
            style={{ width: 360, boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}
          >
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-color)', marginBottom: 6 }}>
              {t('DeleteUser') || 'Delete User'}
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-color-calendar-week)', marginBottom: 16 }}>
              {t('AreYouUser') || `Are you sure you want to delete`}{' '}
              <strong style={{ color: 'var(--text-color)' }}>{deleteState.userName}</strong>?
            </div>
            <div style={{
              fontSize: 12, padding: '8px 12px', borderRadius: 6, marginBottom: 16,
              background: 'var(--bg-alert)', color: 'var(--text-alert)',
              border: '1px solid var(--border-input)',
            }}>
              ⚠ {t('DeleteUserWarning') || 'This will permanently delete the user and all their personal data. Time entries and other records will be preserved.'}
            </div>
            <div className="flex gap-[8px]">
              <button
                type="button"
                onClick={confirmDelete}
                disabled={deleteState.loading}
                style={{
                  background: '#ef4444', color: '#fff', border: 'none',
                  padding: '6px 16px', borderRadius: 5, cursor: 'pointer', fontSize: 13,
                  opacity: deleteState.loading ? 0.6 : 1,
                }}
              >
                {deleteState.loading ? '...' : (t('Delete') || 'Delete')}
              </button>
              <button
                type="button"
                onClick={() => setDeleteState(null)}
                disabled={deleteState.loading}
                className="bg-[var(--btn-user-form)] text-[var(--text-color)] border border-[var(--border-input)] py-[6px] px-[12px] rounded-[5px] cursor-pointer text-[13px]"
              >
                {t('Cancel')}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* — Delete blocked — dependencies by section — */}
      {deleteBlocked && ReactDOM.createPortal(
        <div
          data-portal-modal
          onClick={() => setDeleteBlocked(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 'var(--z-portal)' as any,
            background: 'rgba(0,0,0,0.25)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            className="bg-[var(--bg-form)] rounded-[8px]"
            style={{ width: 480, maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.2)', display: 'flex', flexDirection: 'column' }}
          >
            {/* header */}
            <div style={{ padding: '16px 20px 14px', borderBottom: '1px solid var(--border-input)' }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-color)', marginBottom: 4 }}>
                {t('CannotDelete') || 'Cannot Delete User'}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-color-calendar-week)' }}>
                <strong style={{ color: 'var(--text-color)' }}>{deleteBlocked.userName}</strong>{' '}
                has active dependencies that must be resolved first:
              </div>
            </div>

            {/* dependency list */}
            <div style={{ padding: '12px 20px', display: 'flex', flexDirection: 'column', gap: 10, overflowY: 'auto' }}>
              {deleteBlocked.dependencies.map((dep, i) => (
                <div key={i} style={{ borderRadius: 6, border: '1px solid var(--border-input)', overflow: 'hidden' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 10px', background: 'var(--bg-top)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-color-calendar-week)' }}>
                        {dep.section}
                      </span>
                    </div>
                    <span style={{ fontSize: 11, fontWeight: 700, padding: '1px 7px', borderRadius: 10, background: 'rgba(239,68,68,0.12)', color: '#ef4444' }}>
                      {dep.count}
                    </span>
                  </div>
                  <div style={{ padding: '7px 10px', background: 'var(--btn-user-form)' }}>
                    <div style={{ fontSize: 12, color: 'var(--text-color-calendar-week)', marginBottom: dep.items?.length ? 6 : 0 }}>
                      {dep.reason}
                    </div>
                    {dep.items && dep.items.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {dep.items.slice(0, 8).map((item, j) => (
                          <span key={j} style={{ fontSize: 11, padding: '2px 7px', borderRadius: 4, background: 'var(--bg-form)', border: '1px solid var(--border-input)', color: 'var(--text-color)' }}>
                            {item}
                          </span>
                        ))}
                        {dep.items.length > 8 && (
                          <span style={{ fontSize: 11, padding: '2px 7px', borderRadius: 4, color: 'var(--text-color-calendar-week)' }}>
                            +{dep.items.length - 8} more
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div style={{ padding: '12px 20px', borderTop: '1px solid var(--border-input)' }}>
              <button
                type="button"
                onClick={() => setDeleteBlocked(null)}
                className="bg-[var(--btn-user-btn)] text-[var(--text-color)] border-none py-[6px] px-[16px] rounded-[5px] cursor-pointer text-[13px]"
              >
                {t('Close') || 'Close'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* — Reset password modal — */}
      {reset.userId !== null && ReactDOM.createPortal(
        <div
          data-portal-modal
          onClick={closeReset}
          style={{
            position: 'fixed', inset: 0, zIndex: 'var(--z-portal)' as any,
            background: 'rgba(0,0,0,0.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            className="bg-[var(--bg-form)] rounded-[8px] p-[24px]"
            style={{ width: 340, boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}
          >
            <div className="text-[14px] font-semibold text-[var(--text-color)] mb-[4px]">
              {t('ResetPassword')}
            </div>
            <div className="text-[12px] mb-[16px]" style={{ color: 'var(--text-color-calendar-week)' }}>
              {reset.userName}
            </div>

            <div className="flex flex-col gap-[10px]">
              <input
                type="password"
                placeholder={t('NewPassword') as string}
                value={reset.password}
                autoFocus
                onChange={e => setReset(r => ({ ...r, password: e.target.value, error: '' }))}
                className="bg-[var(--bg-input)] text-[var(--text-color)] rounded-[5px] py-[6px] px-[10px] text-[13px] border border-[var(--border-input)] w-full"
              />
              <input
                type="password"
                placeholder={t('ConfirmPassword') as string}
                value={reset.confirm}
                onChange={e => setReset(r => ({ ...r, confirm: e.target.value, error: '' }))}
                className="bg-[var(--bg-input)] text-[var(--text-color)] rounded-[5px] py-[6px] px-[10px] text-[13px] border border-[var(--border-input)] w-full"
                onKeyDown={e => e.key === 'Enter' && submitReset()}
              />
            </div>

            {reset.error   && <div className="text-[12px] mt-[8px]" style={{ color: '#ef4444' }}>{reset.error}</div>}
            {reset.success && <div className="text-[12px] mt-[8px]" style={{ color: '#22c55e' }}>{t('PasswordChanged')} ✓</div>}

            <div className="flex gap-[8px] mt-[16px]">
              <button type="button" onClick={submitReset} disabled={reset.loading}
                className="bg-[var(--btn-user-btn)] text-[var(--text-color)] border-none py-[6px] px-[16px] rounded-[5px] cursor-pointer hover:bg-[var(--btn-user-hover)] text-[13px]">
                {reset.loading ? '...' : t('Save')}
              </button>
              <button type="button" onClick={closeReset}
                className="bg-[var(--btn-user-form)] text-[var(--text-color)] border border-[var(--border-input)] py-[6px] px-[12px] rounded-[5px] cursor-pointer text-[13px]">
                {t('Cancel')}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default UsersManagement;
